import { useEffect,useMemo,useState } from 'react'
import { listFlashcards } from './flashcards'
import { getLibrary } from './library'
import { listRecentPracticeTests } from './practiceTests'
import type { ExamLite,StudyData } from './studyProgressCore'

// Exam dates (api/src/routes/studyExams.ts) and the data the study dashboards read: the library tree, flashcards with
// their review schedule, recent practice tests and exam dates. Read-only over notes: only their list and lecture dates.
const API='https://api.dawwin.al-ilm.net/study-exams'
export type StudyExam=ExamLite&{notes:string;created_at:string;updated_at:string}
async function call<T>(path='',init:RequestInit={}){const r=await fetch(`${API}${path}`,{...init,credentials:'include',headers:{Accept:'application/json','Content-Type':'application/json',...(init.headers||{})}});if(!r.ok)throw new Error((await r.json().catch(()=>null))?.error||`http_${r.status}`);return(r.status===204?null:await r.json()) as T}
export const listExams=()=>call<{exams:StudyExam[]}>()
export const createExam=(data:{notebook_id:string;title:string;exam_date:string;notes?:string})=>call<{exam:StudyExam}>('',{method:'POST',body:JSON.stringify(data)})
export const updateExam=(id:string,data:Partial<{notebook_id:string;title:string;exam_date:string;notes:string}>)=>call<{exam:StudyExam}>('/'+encodeURIComponent(id),{method:'PATCH',body:JSON.stringify(data)})
export const deleteExam=(id:string)=>call<null>('/'+encodeURIComponent(id),{method:'DELETE'})
export const EXAMS_CHANGED='dawwin:study-exams-change'

type Doc={id:string;title:string;created_at:string;updated_at:string}
export type Loaded=StudyData&{exams:StudyExam[]}
const list=<T,>(v:unknown):T[]=>Array.isArray(v)?v as T[]:[]

/** Loads everything once (and again when exams, flashcards or the library change); each part fails on its own. */
export function useStudyData(documents:Doc[]){
 const[data,setData]=useState<Omit<Loaded,'docs'>|null>(null),[failed,setFailed]=useState(false)
 useEffect(()=>{let live=true
  async function load(){
   const[lib,cards,tests,exams]=await Promise.allSettled([getLibrary(),listFlashcards(),listRecentPracticeTests(100),listExams()])
   if(!live)return
   if(lib.status==='rejected'){setFailed(true);return}
   setFailed(false)
   setData({nodes:list(lib.value?.notebooks),meta:list(lib.value?.documents),cards:cards.status==='fulfilled'?list(cards.value?.cards):[],tests:tests.status==='fulfilled'?list(tests.value?.tests):[],exams:exams.status==='fulfilled'?list(exams.value?.exams):[]})
  }
  void load().catch(()=>{if(live)setFailed(true)})
  const refresh=()=>void load().catch(()=>{})
  for(const e of [EXAMS_CHANGED,'dawwin:library-change'])window.addEventListener(e,refresh)
  return()=>{live=false;for(const e of [EXAMS_CHANGED,'dawwin:library-change'])window.removeEventListener(e,refresh)}
 },[])
 const merged=useMemo(()=>data?{...data,docs:documents}:null,[data,documents])
 return {data:merged,failed}
}

// Revision and Flashcards keep their chosen scope in localStorage; set it, then open the page.
export function openRevision(nodes:{id:string;parent_id:string|null}[],nodeId:string,mode:'due'|'all'|'test'){
 let root=nodes.find(n=>n.id===nodeId);while(root?.parent_id){const p=nodes.find(n=>n.id===root!.parent_id);if(!p)break;root=p}
 try{localStorage.setItem('dawwin:revision:course',root?.id||nodeId);if(root&&root.id!==nodeId)localStorage.setItem('dawwin:revision:module',nodeId);else localStorage.removeItem('dawwin:revision:module');localStorage.setItem('dawwin:revision:mode',mode)}catch{}
 window.location.hash='revision'
}
export function openFlashcardMaker(nodeId:string){
 try{localStorage.setItem('dawwin-flashcards-scope',nodeId);sessionStorage.setItem('dawwin:flashcards:generate-from','scope:'+nodeId)}catch{}
 window.location.hash='flashcards'
}
export const openStudySpace=(id:string)=>{window.location.hash='study-space/'+encodeURIComponent(id)}
